import { Layout } from "@/components/Layout";
import {
  ShoppingCart,
  IndianRupee,
  TrendingUp,
  Package,
  Wallet,
  Users,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useOnlineDashboard, useSalesmanAnalysis } from "@/lib/api";
import { cn } from "@/lib/utils";
import { authService } from "@/lib/api/services/auth.service";

// Format values in lakhs-only compact format without currency symbol
// Always returns value in L (lakhs): 1,00,000 => "1.00L"
const formatLakhsCompactValue = (amount: number): string => {
  const value = Number.isFinite(amount) ? amount : 0;
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const lakhs = absValue / 100000;
  return `${sign}${lakhs.toFixed(2)}L`;
};

// Format INR currency
const formatINR = (n: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

// Modern chart card wrapper
interface ChartCardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  variant?: "default" | "blue" | "purple" | "emerald" | "red";
  className?: string;
  icon?: React.ReactNode;
  showGradientLine?: boolean;
}

const ChartCard = ({ title, subtitle, children, variant = "default", className, icon, showGradientLine = true }: ChartCardProps) => {
  const variants = {
    default: "from-[#0072B5] via-[#0090B5] to-[#00B5B0]",
    blue: "from-[#0072B5] via-[#0090B5] to-[#00B5B0]",
    purple: "from-purple-500 via-pink-400 to-rose-500",
    emerald: "from-emerald-500 via-teal-400 to-[#00B5B0]",
    red: "from-red-500 via-rose-400 to-pink-500",
  };

  return (
    <Card className={cn(
      "relative overflow-hidden rounded-xl",
      "glass-card noise-overlay",
      "border-white/10 dark:border-white/5",
      "transition-shadow duration-500 hover:shadow-[0_8px_40px_-8px_rgba(0,0,0,0.15)]",
      className
    )}>
      {showGradientLine && (
        <div className={cn(
          "absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r",
          variants[variant]
        )} />
      )}
      
      <CardHeader className="relative pb-1.5 pt-3 px-4">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
        {subtitle && (
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        )}
      </CardHeader>
      <CardContent className="relative pt-0 px-4 pb-3 overflow-visible">
        {children}
      </CardContent>
    </Card>
  );
};

const Online = () => {
  // Get firmId from authenticated user
  const user = authService.getCurrentUser();
  const firmId = user?.firmId;

  const { data: onlineData, isLoading: isOnlineLoading, isError: isOnlineError } = useOnlineDashboard({ firmId });
  const { data: salesmanData, isLoading: isSalesmanLoading, isError: isSalesmanError } = useSalesmanAnalysis({ firmId });

  return (
    <Layout>
      <div className="space-y-1">
        <h2 className="text-3xl font-bold">Online Analysis</h2>
        
        <div className="space-y-3 mt-6">
          <div className="grid gap-3 lg:grid-cols-2">
            <ChartCard title="Orders Analysis (Last 6 Months)" variant="blue" showGradientLine={false} icon={<ShoppingCart className="h-4 w-4 text-[#0072B5]" />}>
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="relative overflow-hidden rounded-lg p-2 bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-200/30 dark:border-blue-500/20 group hover:from-blue-500/15 transition-all duration-300">
                  <div className="flex items-center justify-between mb-1">
                    <div className="p-1 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-300/30 dark:border-blue-400/20 group-hover:scale-110 transition-transform duration-300">
                      <Package className="h-3 w-3 text-blue-600 dark:text-blue-400" strokeWidth={2.5} />
                    </div>
                    <p className="text-xs font-medium text-muted-foreground">Orders</p>
                  </div>
                  <p className="text-base font-bold text-foreground text-right">
                    {isOnlineLoading || isOnlineError ? 0 : (onlineData || []).filter(i => i.sNo === 0).reduce((s, i) => s + i.recordsCount, 0)}
                  </p>
                </div>
                <div className="relative overflow-hidden rounded-lg p-2 bg-gradient-to-br from-indigo-500/10 to-transparent border border-indigo-200/30 dark:border-indigo-500/20 group hover:from-indigo-500/15 transition-all duration-300">
                  <div className="flex items-center justify-between mb-1">
                    <div className="p-1 rounded-lg bg-gradient-to-br from-indigo-500/20 to-indigo-600/20 border border-indigo-300/30 dark:border-indigo-400/20 group-hover:scale-110 transition-transform duration-300">
                      <IndianRupee className="h-3 w-3 text-indigo-600 dark:text-indigo-400" strokeWidth={2.5} />
                    </div>
                    <p className="text-xs font-medium text-muted-foreground">Total Value</p>
                  </div>
                  <p className="text-base font-bold text-foreground text-right">
                    {formatLakhsCompactValue((isOnlineLoading || isOnlineError ? 0 : (onlineData || []).filter(i => i.sNo === 0).reduce((s, i) => s + i.totalAmount, 0)))}
                  </p>
                </div>
                <div className="relative overflow-hidden rounded-lg p-2 bg-gradient-to-br from-violet-500/10 to-transparent border border-violet-200/30 dark:border-violet-500/20 group hover:from-violet-500/15 transition-all duration-300">
                  <div className="flex items-center justify-between mb-1">
                    <div className="p-1 rounded-lg bg-gradient-to-br from-violet-500/20 to-violet-600/20 border border-violet-300/30 dark:border-violet-400/20 group-hover:scale-110 transition-transform duration-300">
                      <TrendingUp className="h-3 w-3 text-violet-600 dark:text-violet-400" strokeWidth={2.5} />
                    </div>
                    <p className="text-xs font-medium text-muted-foreground">Avg Value</p>
                  </div>
                  <p className="text-base font-bold text-foreground text-right">{(() => {
                    if (isOnlineLoading || isOnlineError) return formatLakhsCompactValue(0);
                    const arr = (onlineData || []).filter(i => i.sNo === 0);
                    const c = arr.reduce((s, i) => s + i.recordsCount, 0);
                    const t = arr.reduce((s, i) => s + i.totalAmount, 0);
                    return formatLakhsCompactValue(c ? t / c : 0);
                  })()}</p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={(isOnlineLoading || isOnlineError ? [] : (onlineData || []).filter(i => i.sNo === 0)).slice(-6)} barGap={4}>
                  <defs>
                    <linearGradient id="ordersGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--chart-1-light))" stopOpacity={1} />
                      <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0.8} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                  <XAxis 
                    dataKey="monthName" 
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                    tickLine={false}
                  />
                  <YAxis 
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                    tickLine={false}
                    tickFormatter={(value) => formatLakhsCompactValue(value)}
                  />
                  <Tooltip content={({ active, payload, label }: any) => {
                    if (active && payload && payload.length) {
                      const data = payload[0]?.payload;
                      return (
                        <div className="chart-tooltip min-w-[200px]">
                          <p className="text-xs font-semibold text-foreground mb-1.5">{label}</p>
                          <div className="flex items-center justify-between gap-4 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: payload[0]?.color }} />
                              <span className="text-muted-foreground">Orders:</span>
                            </div>
                            <span className="font-medium text-foreground text-right">{data?.recordsCount ?? 0}</span>
                          </div>
                          <div className="flex items-center justify-between gap-4 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: payload[0]?.color }} />
                              <span className="text-muted-foreground">Total Amount:</span>
                            </div>
                            <span className="font-medium text-foreground text-right">{formatLakhsCompactValue(data?.totalAmount ?? 0)}</span>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }} />
                  <Bar dataKey="totalAmount" fill="url(#ordersGradient)" name="Total Amount" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Collections Analysis (Last 6 Months)" variant="emerald" showGradientLine={false} icon={<Wallet className="h-4 w-4 text-emerald-500" />}>
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="relative overflow-hidden rounded-lg p-2 bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-200/30 dark:border-emerald-500/20 group hover:from-emerald-500/15 transition-all duration-300">
                  <div className="flex items-center justify-between mb-1">
                    <div className="p-1 rounded-lg bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 border border-emerald-300/30 dark:border-emerald-400/20 group-hover:scale-110 transition-transform duration-300">
                      <Wallet className="h-3 w-3 text-emerald-600 dark:text-emerald-400" strokeWidth={2.5} />
                    </div>
                    <p className="text-xs font-medium text-muted-foreground">Collections</p>
                  </div>
                  <p className="text-base font-bold text-foreground text-right">
                    {isOnlineLoading || isOnlineError ? 0 : (onlineData || []).filter(i => i.sNo === 1).reduce((s, i) => s + i.recordsCount, 0)}
                  </p>
                </div>
                <div className="relative overflow-hidden rounded-lg p-2 bg-gradient-to-br from-teal-500/10 to-transparent border border-teal-200/30 dark:border-teal-500/20 group hover:from-teal-500/15 transition-all duration-300">
                  <div className="flex items-center justify-between mb-1">
                    <div className="p-1 rounded-lg bg-gradient-to-br from-teal-500/20 to-teal-600/20 border border-teal-300/30 dark:border-teal-400/20 group-hover:scale-110 transition-transform duration-300">
                      <IndianRupee className="h-3 w-3 text-teal-600 dark:text-teal-400" strokeWidth={2.5} />
                    </div>
                    <p className="text-xs font-medium text-muted-foreground">Total Value</p>
                  </div>
                  <p className="text-base font-bold text-foreground text-right">
                    {formatLakhsCompactValue((isOnlineLoading || isOnlineError ? 0 : (onlineData || []).filter(i => i.sNo === 1).reduce((s, i) => s + i.totalAmount, 0)))}
                  </p>
                </div>
                <div className="relative overflow-hidden rounded-lg p-2 bg-gradient-to-br from-cyan-500/10 to-transparent border border-cyan-200/30 dark:border-cyan-500/20 group hover:from-cyan-500/15 transition-all duration-300">
                  <div className="flex items-center justify-between mb-1">
                    <div className="p-1 rounded-lg bg-gradient-to-br from-cyan-500/20 to-cyan-600/20 border border-cyan-300/30 dark:border-cyan-400/20 group-hover:scale-110 transition-transform duration-300">
                      <TrendingUp className="h-3 w-3 text-cyan-600 dark:text-cyan-400" strokeWidth={2.5} />
                    </div>
                    <p className="text-xs font-medium text-muted-foreground">Avg Value</p>
                  </div>
                  <p className="text-base font-bold text-foreground text-right">{(() => {
                    if (isOnlineLoading || isOnlineError) return formatLakhsCompactValue(0);
                    const arr = (onlineData || []).filter(i => i.sNo === 1);
                    const c = arr.reduce((s, i) => s + i.recordsCount, 0);
                    const t = arr.reduce((s, i) => s + i.totalAmount, 0);
                    return formatLakhsCompactValue(c ? t / c : 0);
                  })()}</p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={(isOnlineLoading || isOnlineError ? [] : (onlineData || []).filter(i => i.sNo === 1)).slice(-6)} barGap={4}>
                  <defs>
                    <linearGradient id="collectionsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--chart-2-light))" stopOpacity={1} />
                      <stop offset="100%" stopColor="hsl(var(--chart-2))" stopOpacity={0.8} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                  <XAxis 
                    dataKey="monthName" 
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                    tickLine={false}
                  />
                  <YAxis 
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                    tickLine={false}
                    tickFormatter={(value) => formatLakhsCompactValue(value)}
                  />
                  <Tooltip content={({ active, payload, label }: any) => {
                    if (active && payload && payload.length) {
                      const data = payload[0]?.payload;
                      return (
                        <div className="chart-tooltip min-w-[200px]">
                          <p className="text-xs font-semibold text-foreground mb-1.5">{label}</p>
                          <div className="flex items-center justify-between gap-4 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: payload[0]?.color }} />
                              <span className="text-muted-foreground">Collections:</span>
                            </div>
                            <span className="font-medium text-foreground text-right">{data?.recordsCount ?? 0}</span>
                          </div>
                          <div className="flex items-center justify-between gap-4 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: payload[0]?.color }} />
                              <span className="text-muted-foreground">Total Amount:</span>
                            </div>
                            <span className="font-medium text-foreground text-right">{formatLakhsCompactValue(data?.totalAmount ?? 0)}</span>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }} />
                  <Bar dataKey="totalAmount" fill="url(#collectionsGradient)" name="Total Amount" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <Card className="relative overflow-hidden rounded-xl glass-card noise-overlay border-white/10 dark:border-white/5 bg-gradient-to-br from-violet-500/10 to-purple-500/5">
            <CardHeader className="relative pb-2 pt-3 sm:pt-4 px-3 sm:px-4">
              <CardTitle className="text-sm sm:text-base font-semibold flex items-center gap-1.5 sm:gap-2">
                <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-violet-500" />
                <span className="truncate flex items-center gap-2">
                  <span>Salesman Wise Analysis</span>
                  <span className="text-[10px] sm:text-xs font-semibold text-violet-700 dark:text-violet-300 bg-violet-50/90 dark:bg-violet-500/10 px-2 py-0.5 rounded-full border border-violet-200/60 dark:border-violet-500/30">
                    (Current Month - ONLINE Only)
                  </span>
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="relative px-2 sm:px-4 pb-3 sm:pb-4 pt-0">
              <div className="overflow-hidden rounded-lg border border-border/50">
                <div className="overflow-x-auto -mx-2 sm:mx-0">
                <table className="w-full min-w-[640px]">
                  <thead>
                    <tr className="bg-muted">
                      <th className="sticky top-0 bg-muted z-10 text-left px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">#</th>
                      <th className="sticky top-0 bg-muted z-10 text-left px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Name</th>
                      <th className="sticky top-0 bg-muted z-10 text-left px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden sm:table-cell">Mobile</th>
                      <th className="sticky top-0 bg-muted z-10 text-right px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Orders</th>
                      <th className="sticky top-0 bg-muted z-10 text-right px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Order Value</th>
                      <th className="sticky top-0 bg-muted z-10 text-right px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Collections</th>
                      <th className="sticky top-0 bg-muted z-10 text-right px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Collection Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {isSalesmanLoading && (
                      <tr>
                        <td className="px-3 py-4 text-sm text-muted-foreground text-center" colSpan={7}>
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                            Loading...
                          </div>
                        </td>
                      </tr>
                    )}
                    {isSalesmanError && !isSalesmanLoading && (
                      <tr>
                        <td className="px-3 py-4 text-sm text-destructive text-center" colSpan={7}>Failed to load data</td>
                      </tr>
                    )}
                    {!isSalesmanLoading && !isSalesmanError && (salesmanData || []).map((item, idx) => (
                      <tr 
                        key={idx} 
                        className="group hover:bg-gradient-to-r hover:from-violet-500/5 hover:to-transparent transition-colors duration-200"
                      >
                        <td className="px-2 sm:px-3 py-1 sm:py-1.5">
                          <span className="inline-flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-muted text-[10px] sm:text-xs font-medium">
                            {idx + 1}
                          </span>
                        </td>
                        <td className="px-2 sm:px-3 py-1 sm:py-1.5">
                          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                            <span className="text-xs sm:text-sm font-medium text-foreground truncate">{item.salesManname}</span>
                            {item.salesManCode && (
                              <Badge variant="outline" className="shrink-0 text-[9px] sm:text-[10px] px-1 sm:px-1.5 py-0 h-3.5 sm:h-4 bg-muted/50 border-border/50 text-muted-foreground font-normal">
                                {item.salesManCode}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-1 sm:py-1.5 hidden sm:table-cell">
                          <span className="text-xs sm:text-sm text-muted-foreground font-mono">{item.mobileNo}</span>
                        </td>
                        <td className="px-2 sm:px-3 py-1 sm:py-1.5 text-right">
                          <span className="inline-flex items-center justify-center min-w-[1.25rem] sm:min-w-[1.5rem] px-1 sm:px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-[10px] sm:text-xs font-medium">
                            {item.orderCount}
                          </span>
                        </td>
                        <td className="px-2 sm:px-3 py-1 sm:py-1.5 text-right">
                          <span className="text-xs sm:text-sm font-semibold text-foreground">{formatINR(Number(item.totalAmount))}</span>
                        </td>
                        <td className="px-2 sm:px-3 py-1 sm:py-1.5 text-right">
                          <span className="inline-flex items-center justify-center min-w-[1.25rem] sm:min-w-[1.5rem] px-1 sm:px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] sm:text-xs font-medium">
                            {item.receiptsCount}
                          </span>
                        </td>
                        <td className="px-2 sm:px-3 py-1 sm:py-1.5 text-right">
                          <span className="text-xs sm:text-sm font-semibold text-foreground">{formatINR(Number(item.receiptsAmount))}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Online;

